import React, { useState } from "react";
import useFetch from "@/lib/useFetch";
import { GlassCard, PageTitle, LuxuryButton, EmptyState } from "@/components/lux";
import api, { apiError, money } from "@/lib/api";
import { toast } from "sonner";
import { Gift, Copy, Ban, UserPlus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const inp = "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-[var(--gold)]/50";
const DENOMS = [30, 50, 100, 200, 500, 1000];
const STATUS_COLORS = { AVAILABLE: "#22c55e", ASSIGNED: "#e6c15a", ACTIVATED: "#ec4899", REVOKED: "#ef4444", EXPIRED: "#9aa3b2" };

export default function AdminPinkPins() {
  const { data: report, reload: reloadReport } = useFetch("/admin/pink-pins/report");
  const [filter, setFilter] = useState("");
  const { data, loading, reload } = useFetch(`/admin/pink-pins${filter ? `?status=${filter}` : ""}`);
  const [denom, setDenom] = useState(100);
  const [count, setCount] = useState(1);
  const [expiry, setExpiry] = useState("");
  const [busy, setBusy] = useState(false);
  const [createdCodes, setCreatedCodes] = useState(null);
  const [assignPin, setAssignPin] = useState(null);
  const [assignMember, setAssignMember] = useState("");

  const pins = data?.pins || [];
  const summary = report?.summary || {};
  const denomBreak = report?.denominations || {};
  const activated = report?.activated || [];

  const refresh = () => { reload(); reloadReport(); };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { denomination: Number(denom), count: Number(count) || 1 };
      if (expiry) body.expiry_days = Number(expiry);
      const { data } = await api.post("/admin/pink-pins", body);
      setCreatedCodes(data.pins || []);
      toast.success(data.message);
      refresh();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const doAssign = async () => {
    if (!assignMember.trim()) return toast.error("Enter a Member ID");
    setBusy(true);
    try {
      await api.post(`/admin/pink-pins/${assignPin.pin_id}/assign`, { member_id: assignMember.trim() });
      toast.success("Pin assigned"); setAssignPin(null); setAssignMember(""); refresh();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const revoke = async (pin_id) => {
    if (!window.confirm("Revoke this Pink Pin? It cannot be used after revoking.")) return;
    try { await api.post(`/admin/pink-pins/${pin_id}/revoke`); toast.success("Revoked"); refresh(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Failed"); }
  };

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };

  const Card = ({ label, value, color }) => (
    <div className="rounded-xl glass-2 p-4 text-center" data-testid={`pinkpin-stat-${label.toLowerCase()}`}>
      <p className="text-2xl font-bold" style={{ color: color || "#fff" }}>{value ?? 0}</p>
      <p className="text-[11px] uppercase tracking-wide text-[var(--text-dim)]">{label}</p>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-pink-pins">
      <PageTitle title="Pink Pins" subtitle="Admin-controlled FREE activation pins. Pink Pin accounts get a 5X earning cap and are EXCLUDED from upline direct/binary/reward/salary/rank business." />

      <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
        <Card label="Total" value={summary.total} />
        <Card label="Available" value={summary.AVAILABLE} color={STATUS_COLORS.AVAILABLE} />
        <Card label="Assigned" value={summary.ASSIGNED} color={STATUS_COLORS.ASSIGNED} />
        <Card label="Activated" value={summary.ACTIVATED} color={STATUS_COLORS.ACTIVATED} />
        <Card label="Revoked" value={summary.REVOKED} color={STATUS_COLORS.REVOKED} />
        <Card label="Expired" value={summary.EXPIRED} color={STATUS_COLORS.EXPIRED} />
      </div>

      <GlassCard className="p-6">
        <div className="mb-4 flex items-center gap-2"><Gift size={18} className="text-[#ec4899]" /><h3 className="font-semibold text-white">Create Pink Pins</h3></div>
        <form onSubmit={create} className="grid gap-3 sm:grid-cols-4" data-testid="pinkpin-create-form">
          <div>
            <label className="mb-1 block text-xs text-[var(--text-dim)]">Denomination</label>
            <select className={inp} value={denom} onChange={(e) => setDenom(e.target.value)} data-testid="pinkpin-denom">
              {DENOMS.map((d) => <option key={d} value={d}>${d}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-[var(--text-dim)]">Quantity</label>
            <input type="number" min="1" max="100" className={inp} value={count} onChange={(e) => setCount(e.target.value)} data-testid="pinkpin-count" />
          </div>
          <div>
            <label className="mb-1 block text-xs text-[var(--text-dim)]">Expiry (days, optional)</label>
            <input type="number" min="1" className={inp} placeholder="No expiry" value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="pinkpin-expiry" />
          </div>
          <div className="flex items-end">
            <LuxuryButton type="submit" variant="gold" disabled={busy} className="w-full" data-testid="pinkpin-create-btn"><Gift size={15} className="mr-1 inline" /> Generate</LuxuryButton>
          </div>
        </form>
      </GlassCard>

      <GlassCard className="p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-semibold text-white">All Pink Pins</h3>
          <select className={inp + " w-auto"} value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="pinkpin-filter">
            <option value="">All statuses</option>
            {["AVAILABLE", "ASSIGNED", "ACTIVATED", "REVOKED", "EXPIRED"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        {loading ? <div className="skeleton h-32" /> : pins.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-white">
              <thead><tr className="text-left text-[var(--text-dim)]">
                <th className="p-2">Pin ID</th><th className="p-2">Code</th><th className="p-2">Amount</th><th className="p-2">Status</th><th className="p-2">Assigned</th><th className="p-2">Activated By</th><th className="p-2">Action</th>
              </tr></thead>
              <tbody>
                {pins.map((p) => (
                  <tr key={p.pin_id} className="border-t border-white/5" data-testid={`pinkpin-row-${p.pin_id}`}>
                    <td className="p-2 font-mono text-xs">{p.pin_id}</td>
                    <td className="p-2 font-mono text-xs text-[var(--text-dim)]">{p.code_masked}</td>
                    <td className="p-2 font-semibold gold-text">${money(p.denomination, 0)}</td>
                    <td className="p-2"><span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: (STATUS_COLORS[p.status] || "#888") + "22", color: STATUS_COLORS[p.status] }}>{p.status}</span></td>
                    <td className="p-2 text-xs">{p.assigned_to || "—"}</td>
                    <td className="p-2 text-xs">{p.activated_by || "—"}</td>
                    <td className="p-2">
                      <div className="flex gap-1.5">
                        {p.status === "AVAILABLE" && <button onClick={() => { setAssignPin(p); setAssignMember(""); }} className="btn3d btn3d-dark rounded-lg px-2 py-1 text-xs" data-testid={`pinkpin-assign-${p.pin_id}`}><UserPlus size={13} /></button>}
                        {(p.status === "AVAILABLE" || p.status === "ASSIGNED") && <button onClick={() => revoke(p.pin_id)} className="btn3d btn3d-maroon rounded-lg px-2 py-1 text-xs text-white" data-testid={`pinkpin-revoke-${p.pin_id}`}><Ban size={13} /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <EmptyState icon={Gift} title="No Pink Pins yet" />}
      </GlassCard>

      <GlassCard className="p-6">
        <h3 className="mb-4 font-semibold text-white">Activated Pink Pin Members</h3>
        {activated.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-white">
              <thead><tr className="text-left text-[var(--text-dim)]">
                <th className="p-2">Member</th><th className="p-2">Sponsor</th><th className="p-2">Denom</th><th className="p-2">Cap (5X)</th><th className="p-2">Used</th><th className="p-2">Remaining</th><th className="p-2">Pool</th><th className="p-2">Activated</th>
              </tr></thead>
              <tbody>
                {activated.map((a) => (
                  <tr key={a.member_id} className="border-t border-white/5" data-testid={`pinkpin-activated-${a.member_id}`}>
                    <td className="p-2">{a.member_id}<span className="block text-[11px] text-[var(--text-dim)]">{a.name}</span></td>
                    <td className="p-2 text-xs">{a.sponsor || "—"}</td>
                    <td className="p-2">${money(a.denomination, 0)}</td>
                    <td className="p-2">${money(a.cap, 0)}</td>
                    <td className="p-2">${money(a.used)}</td>
                    <td className="p-2 font-semibold" style={{ color: "#22c55e" }}>${money(a.remaining)}</td>
                    <td className="p-2 text-xs">{a.global_pool_eligible ? "Eligible" : "—"}</td>
                    <td className="p-2 text-xs text-[var(--text-dim)]">{(a.activated_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-sm text-[var(--text-dim)]">No activated Pink Pins yet.</p>}
        <div className="mt-3 flex flex-wrap gap-2 text-xs text-[var(--text-dim)]">
          {DENOMS.map((d) => <span key={d} className="rounded-full glass-2 px-3 py-1">${d}: <b className="text-white">{denomBreak[String(d)] || 0}</b></span>)}
        </div>
      </GlassCard>

      {/* Created codes (shown ONCE) */}
      <Dialog open={!!createdCodes} onOpenChange={(o) => !o && setCreatedCodes(null)}>
        <DialogContent className="border-white/10 bg-[var(--bg-2)] text-white" data-testid="pinkpin-codes-modal">
          <DialogHeader><DialogTitle>Pink Pin Codes — copy now</DialogTitle></DialogHeader>
          <p className="text-xs text-[var(--text-dim)]">These full codes are shown only once. Distribute them securely to the selected members.</p>
          <div className="mt-2 max-h-72 space-y-2 overflow-y-auto">
            {(createdCodes || []).map((c) => (
              <div key={c.pin_id} className="flex items-center justify-between gap-2 rounded-lg glass-2 p-2">
                <div><p className="font-mono text-sm font-bold text-[#ec4899]">{c.code}</p><p className="text-[11px] text-[var(--text-dim)]">${c.denomination} · {c.pin_id}</p></div>
                <button onClick={() => copy(c.code)} className="btn3d btn3d-gold rounded-lg px-2 py-1 text-xs text-black"><Copy size={13} /></button>
              </div>
            ))}
          </div>
          <LuxuryButton variant="gold" className="mt-2 w-full" onClick={() => setCreatedCodes(null)}>Done</LuxuryButton>
        </DialogContent>
      </Dialog>

      {/* Assign dialog */}
      <Dialog open={!!assignPin} onOpenChange={(o) => !o && setAssignPin(null)}>
        <DialogContent className="border-white/10 bg-[var(--bg-2)] text-white" data-testid="pinkpin-assign-modal">
          <DialogHeader><DialogTitle>Assign Pink Pin</DialogTitle></DialogHeader>
          {assignPin && <p className="text-xs text-[var(--text-dim)]">${money(assignPin.denomination, 0)} pin · {assignPin.pin_id}</p>}
          <input className={inp} placeholder="Member ID (e.g. AF100045)" value={assignMember} onChange={(e) => setAssignMember(e.target.value)} data-testid="pinkpin-assign-member" />
          <div className="flex gap-2">
            <LuxuryButton variant="dark" className="flex-1" onClick={() => setAssignPin(null)}>Cancel</LuxuryButton>
            <LuxuryButton variant="gold" className="flex-1" disabled={busy} onClick={doAssign} data-testid="pinkpin-assign-confirm">Assign</LuxuryButton>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
